import { useEffect } from 'react';

const SITE_URL = 'https://hellacamping.com';

const DEFAULT_TITLE = 'HellaCamping - Find Open Campsites Nationwide';
const DEFAULT_DESCRIPTION =
  'Find and track open campsites across state and national parks nationwide. Get notified when your favorite campgrounds have availability.';
const DEFAULT_OG_IMAGE = '/og-image.png';

interface PageMetaOptions {
  title?: string;
  description?: string;
  canonicalPath?: string;
  ogImage?: string;
}

function findOrCreateMeta(attr: string, value: string): HTMLMetaElement {
  const selector = `meta[${attr}="${value}"]`;
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr.split('=')[0] === 'property' ? 'property' : 'name', value);
    document.head.appendChild(el);
  }
  return el;
}

function findOrCreateLink(rel: string): HTMLLinkElement {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  return el;
}

export function usePageMeta({ title, description, canonicalPath, ogImage }: PageMetaOptions) {
  useEffect(() => {
    const resolvedTitle = title ?? DEFAULT_TITLE;
    const resolvedDescription = description ?? DEFAULT_DESCRIPTION;
    const resolvedOgImage = ogImage ?? DEFAULT_OG_IMAGE;
    const canonicalUrl = canonicalPath ? `${SITE_URL}${canonicalPath}` : undefined;

    // Title
    const prevTitle = document.title;
    document.title = resolvedTitle;

    // Meta description
    const descMeta = findOrCreateMeta('name', 'description');
    const prevDesc = descMeta.getAttribute('content');
    descMeta.setAttribute('content', resolvedDescription);

    // OG tags
    const ogTitle = findOrCreateMeta('property', 'og:title');
    const prevOgTitle = ogTitle.getAttribute('content');
    ogTitle.setAttribute('content', resolvedTitle);

    const ogDesc = findOrCreateMeta('property', 'og:description');
    const prevOgDesc = ogDesc.getAttribute('content');
    ogDesc.setAttribute('content', resolvedDescription);

    const ogUrl = findOrCreateMeta('property', 'og:url');
    const prevOgUrl = ogUrl.getAttribute('content');
    if (canonicalUrl) ogUrl.setAttribute('content', canonicalUrl);

    const ogImg = findOrCreateMeta('property', 'og:image');
    const prevOgImg = ogImg.getAttribute('content');
    ogImg.setAttribute('content', resolvedOgImage);

    // Twitter tags
    const twTitle = findOrCreateMeta('name', 'twitter:title');
    const prevTwTitle = twTitle.getAttribute('content');
    twTitle.setAttribute('content', resolvedTitle);

    const twDesc = findOrCreateMeta('name', 'twitter:description');
    const prevTwDesc = twDesc.getAttribute('content');
    twDesc.setAttribute('content', resolvedDescription);

    const twImg = findOrCreateMeta('name', 'twitter:image');
    const prevTwImg = twImg.getAttribute('content');
    twImg.setAttribute('content', resolvedOgImage);

    // Canonical link
    let canonical: HTMLLinkElement | null = null;
    let prevCanonicalHref: string | null = null;
    if (canonicalUrl) {
      canonical = findOrCreateLink('canonical');
      prevCanonicalHref = canonical.getAttribute('href');
      canonical.setAttribute('href', canonicalUrl);
    }

    return () => {
      document.title = prevTitle;
      descMeta.setAttribute('content', prevDesc ?? DEFAULT_DESCRIPTION);
      ogTitle.setAttribute('content', prevOgTitle ?? DEFAULT_TITLE);
      ogDesc.setAttribute('content', prevOgDesc ?? DEFAULT_DESCRIPTION);
      ogUrl.setAttribute('content', prevOgUrl ?? '');
      ogImg.setAttribute('content', prevOgImg ?? DEFAULT_OG_IMAGE);
      twTitle.setAttribute('content', prevTwTitle ?? DEFAULT_TITLE);
      twDesc.setAttribute('content', prevTwDesc ?? DEFAULT_DESCRIPTION);
      twImg.setAttribute('content', prevTwImg ?? DEFAULT_OG_IMAGE);
      if (canonical) {
        if (prevCanonicalHref) {
          canonical.setAttribute('href', prevCanonicalHref);
        } else {
          canonical.remove();
        }
      }
    };
  }, [title, description, canonicalPath, ogImage]);
}
